import React, { useEffect,useState } from 'react';
import {
    Box,
    Grid,
    TextField,
    FormControlLabel,
    Switch,
    Alert,
    InputAdornment,
    Typography, Divider,
} from '@mui/material';
import { Title as TitleIcon, Description as DescriptionIcon } from '@mui/icons-material';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useSurveyStore, QUESTION_TYPES, TEXT_INPUT_SUBTYPES } from '../../surveyutilities/surveyStore';

const TextBlockProperties = ({ questionId }) => {
    const { getQuestionById, updateQuestion, validateName } = useSurveyStore();
    const question = getQuestionById(questionId);
    const [nameError, setNameError] = useState('');
    const [localName, setLocalName] = useState(question?.name || '');

    // Helper for updating store immediately
    const handleUpdate = (property, value) => {
        if (property === 'name') {
            const validation = validateName(value, questionId);
            setNameError(validation.error);

            // Only update if valid
            updateQuestion(questionId, { [property]: value });

        } else {
            updateQuestion(questionId, { [property]: value });
        }
    };

// Sync local state with store when question changes
    useEffect(() => {
        setLocalName(question?.name || '');
    }, [question?.name]);
    const handleNameChange = (value) => {
        setLocalName(value);
        const validation = validateName(value, questionId);
        setNameError(validation.error);

        // Only update store if validation passes
        if (validation.isValid) {
            updateQuestion(questionId, { name: value });
        }
    };
    if (!question) return <Typography color="error">Question not found</Typography>;

    const formattedType = (subType) => {
        switch (subType) {
            case 'text':
                return 'Text';
            case 'email':
                return 'Email';
            case 'password':
                return 'Password';
            case 'url':
                return 'URL';
            case 'tel':
                return 'Telephone';
            case 'number':
                return 'Number';
            case 'range':
                return 'Range';
            case 'color':
                return 'Color';
            case 'date':
                return 'Date';
            case 'time':
                return 'Time';
            case 'datetime-local':
                return 'Date and Time';
            case 'week':
                return 'Week';
            case 'month':
                return 'Month';
            default:
                return 'Text';
        }
    };
    return (
        <Grid container spacing={2}>
            {/* name */}
            <Grid size={{xs:12}}>
                <TextField
                    fullWidth
                    size="small"
                    label="Name (not visible to public, unique)"
                    value={localName}
                    onChange={(e) => handleNameChange(e.target.value)}
                    error={!!nameError}
                    helperText={nameError}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <VisibilityOffIcon />
                            </InputAdornment>
                        )
                    }}
                />
            </Grid>
            {/* Title */}
            <Grid size={{xs:12}}>
                <TextField
                    fullWidth
                    size="small"
                    label="Question Title"
                    value={question.title || ''}
                    onChange={(e) => handleUpdate('title', e.target.value)}
                    InputProps={{ startAdornment: <InputAdornment position="start"><TitleIcon /></InputAdornment> }}
                />
            </Grid>

            {/* Description */}
            <Grid size={{xs:12}}>
                <TextField
                    fullWidth
                    size="small"
                    label="Description"
                    multiline
                    rows={2}
                    value={question.description || ''}
                    onChange={(e) => handleUpdate('description', e.target.value)}
                    InputProps={{ startAdornment: <InputAdornment position="start"><DescriptionIcon /></InputAdornment> }}
                />
            </Grid>

            {/* Input Type Selector */}
            <Grid size={{xs:12}}>
                <TextField
                    select
                    fullWidth
                    size="small"
                    label="Input Type"
                    value={question.inputType || 'text'}
                    onChange={(e) => handleUpdate('inputType', e.target.value)}
                    SelectProps={{ native: true }}
                >
                    {TEXT_INPUT_SUBTYPES.map((type) => (
                        <option key={type} value={type}>
                            {formattedType(type)}
                        </option>
                    ))}
                </TextField>
            </Grid>

            {/* Placeholder (for text/email/password/url) */}
            {['text', 'email', 'password', 'url', 'tel'].includes(question.inputType) && (
                <Grid size={{xs:12}}>
                    <TextField
                        fullWidth
                        size="small"
                        label="Placeholder"
                        value={question.placeholder || ''}
                        onChange={(e) => handleUpdate('placeholder', e.target.value)}
                    />
                </Grid>
            )}

            {/* Min / Max and Error Messages (for number/range) */}
            {['number', 'range'].includes(question.inputType) && (
                <>
                    <Grid size={{xs:12}}>
                        <TextField
                            fullWidth
                            size="small"
                            type="number"
                            label="Minimum Value"
                            value={question.minValue ?? ''}
                            onChange={(e) => handleUpdate('minValue', e.target.value ? parseFloat(e.target.value) : null)}
                        />
                    </Grid>
                    <Grid size={{xs:12}}>
                        <TextField
                            fullWidth
                            size="small"
                            type="number"
                            label="Maximum Value"
                            value={question.maxValue ?? ''}
                            onChange={(e) => handleUpdate('maxValue', e.target.value ? parseFloat(e.target.value) : null)}
                        />
                    </Grid>
                    <Grid size={{xs:12}}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Min Error Message"
                            value={question.minErrorMessage || ''}
                            onChange={(e) => handleUpdate('minErrorMessage', e.target.value)}
                        />
                    </Grid>
                    <Grid size={{xs:12}}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Max Error Message"
                            value={question.maxErrorMessage || ''}
                            onChange={(e) => handleUpdate('maxErrorMessage', e.target.value)}
                        />
                    </Grid>
                </>
            )}

            {/* Max Length (for text/email/password) */}
            {['text', 'email', 'password'].includes(question.inputType) && (
                <Grid size={{xs:12}}>
                    <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label="Maximum Length"
                        value={question.maxLength ?? ''}
                        onChange={(e) => handleUpdate('maxLength', e.target.value ? parseInt(e.target.value) : null)}
                    />
                </Grid>
            )}

            {/* Required Switch */}
            <Grid size={{xs:12}}>
                <FormControlLabel
                    control={
                        <Switch
                            checked={question.required || false}
                            onChange={(e) => handleUpdate('required', e.target.checked)}
                            color="primary"
                        />
                    }
                    label="Required Question"
                />
            </Grid>
            {/* Display Id of selected Question */}
            <Grid size={{ xs: 12 }}>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="caption" gutterBottom sx={{ mt: 2 }}>
                    ID: {questionId}
                </Typography>

            </Grid>
        </Grid>
    );
};

export default TextBlockProperties;
